import {
  Folder,
  FolderOpen,
  User,
  LogOut,
  Settings,
  ChevronLeft,
  ChevronRight,
  Home,
  Pin,
  PinOff,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useQuery } from 'convex/react'
import { useAuthActions } from '@convex-dev/auth/react'
import { api } from '../../../../convex/_generated/api'
import { useNavigate, useLocation } from 'react-router-dom'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { toast } from 'sonner'
import { AuthErrorHandling, logError } from '@/utils/errorHandling'
import { useState, useEffect, useRef, useCallback } from 'react'
import useSidebarStore from '@/stores/useSidebarStore'
import useResponsive from '@/hooks/useResponsive'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const PrimarySidebar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  // Use chef pattern - direct query instead of custom hook
  const user = useQuery(api.auth.currentUser)
  const { signOut } = useAuthActions()
  const {
    primary,
    togglePrimary,
    setHoverExpanded,
    setAutoCollapse,
    isMobile: storeIsMobile,
    isTablet: storeIsTablet,
  } = useSidebarStore()
  const { isMobile, isTablet } = useResponsive()

  const [isSigningOut, setIsSigningOut] = useState(false)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleSignOut = useCallback(async () => {
    // Add confirmation dialog
    const confirmed = window.confirm('Are you sure you want to sign out?')
    if (!confirmed) return

    try {
      setIsSigningOut(true)
      await signOut()
      toast.success('Signed out successfully')
      navigate('/auth/login')
    } catch (error) {
      logError(error, 'Sign out')

      // Use centralized error handling
      const errorMessage = AuthErrorHandling.signOut(error)
      toast.error(errorMessage)

      // If sign out fails, still try to navigate to login as a fallback
      setTimeout(() => {
        navigate('/auth/login')
      }, 2000)
    } finally {
      setIsSigningOut(false)
    }
  }, [signOut, navigate])

  // Add keyboard shortcut for sign out (Ctrl+Shift+Q)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'Q' && user) {
        event.preventDefault()
        handleSignOut()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [user, handleSignOut])

  // Auto-collapse hover handlers

  // Helper function to check if auto-collapse is active
  const isAutoCollapseActive = useCallback(() => {
    return primary.autoCollapse && !isMobile && !isTablet
  }, [primary.autoCollapse, isMobile, isTablet])

  // Helper function to clear hover timeout
  const clearHoverTimeout = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }, [])

  // Helper function to schedule collapse with delay
  const scheduleCollapse = useCallback(() => {
    clearHoverTimeout()
    hoverTimeoutRef.current = setTimeout(() => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PrimarySidebar] Executing scheduled collapse')
      }
      setHoverExpanded(false)
      hoverTimeoutRef.current = null
    }, 300)
  }, [clearHoverTimeout, setHoverExpanded])

  const handleMouseEnter = useCallback(() => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PrimarySidebar] 🎯 MOUSE ENTER EVENT TRIGGERED', {
          autoCollapse: primary.autoCollapse,
          collapsed: primary.collapsed,
          hoverExpanded: primary.hoverExpanded,
          isMobile,
          isTablet,
          isAutoCollapseActive: isAutoCollapseActive(),
        })
      }

      if (isAutoCollapseActive()) {
        if (process.env.NODE_ENV === 'development') {
          console.log(
            '[PrimarySidebar] ✅ Auto-collapse is active - expanding sidebar'
          )
        }
        clearHoverTimeout()
        setHoverExpanded(true)
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log(
            '[PrimarySidebar] ❌ Auto-collapse is NOT active - no expansion',
            {
              autoCollapse: primary.autoCollapse,
              isMobile,
              isTablet,
              reason: !primary.autoCollapse
                ? 'autoCollapse disabled'
                : isMobile
                  ? 'on mobile'
                  : isTablet
                    ? 'on tablet'
                    : 'unknown',
            }
          )
        }
      }
    } catch (error) {
      console.error('[PrimarySidebar] Error in handleMouseEnter:', error)
      // Try to clear any stuck state
      clearHoverTimeout()
    }
  }, [isAutoCollapseActive, clearHoverTimeout, setHoverExpanded])

  const handleMouseLeave = useCallback(() => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PrimarySidebar] 🎯 MOUSE LEAVE EVENT TRIGGERED', {
          autoCollapse: primary.autoCollapse,
          collapsed: primary.collapsed,
          hoverExpanded: primary.hoverExpanded,
          isMobile,
          isTablet,
          isAutoCollapseActive: isAutoCollapseActive(),
        })
      }

      if (isAutoCollapseActive()) {
        if (process.env.NODE_ENV === 'development') {
          console.log(
            '[PrimarySidebar] ✅ Auto-collapse is active - scheduling collapse in 300ms'
          )
        }
        scheduleCollapse()
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log(
            '[PrimarySidebar] ❌ Auto-collapse is NOT active - no collapse scheduled'
          )
        }
      }
    } catch (error) {
      console.error('[PrimarySidebar] Error in handleMouseLeave:', error)
      // Try to clear any stuck state
      clearHoverTimeout()
    }
  }, [isAutoCollapseActive, scheduleCollapse])

  const handleFocus = useCallback(() => {
    try {
      if (isAutoCollapseActive()) {
        if (process.env.NODE_ENV === 'development') {
          console.log(
            '[PrimarySidebar] Focus - expanding sidebar for keyboard navigation'
          )
        }
        clearHoverTimeout()
        setHoverExpanded(true)
      }
    } catch (error) {
      console.error('[PrimarySidebar] Error in handleFocus:', error)
      // Try to clear any stuck state
      clearHoverTimeout()
    }
  }, [isAutoCollapseActive, clearHoverTimeout, setHoverExpanded])

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      try {
        if (isAutoCollapseActive()) {
          // Only collapse if focus is leaving the sidebar entirely
          const currentTarget = e.currentTarget
          const relatedTarget = e.relatedTarget as Node

          if (!currentTarget.contains(relatedTarget)) {
            if (process.env.NODE_ENV === 'development') {
              console.log(
                '[PrimarySidebar] Blur - scheduling collapse in 300ms'
              )
            }
            scheduleCollapse()
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log(
                '[PrimarySidebar] Blur - staying within sidebar, no collapse'
              )
            }
          }
        }
      } catch (error) {
        console.error('[PrimarySidebar] Error in handleBlur:', error)
        // Try to clear any stuck state
        clearHoverTimeout()
      }
    },
    [isAutoCollapseActive, scheduleCollapse, clearHoverTimeout]
  )

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      clearHoverTimeout()
    }
  }, [clearHoverTimeout])

  // CRITICAL FIX: Clear hover timeout when responsive state changes
  useEffect(() => {
    // If we're changing to mobile/tablet or auto-collapse is disabled, clear any pending timeout
    if (isMobile || isTablet || !primary.autoCollapse) {
      clearHoverTimeout()
      if (process.env.NODE_ENV === 'development') {
        console.log(
          '[PrimarySidebar] Clearing hover timeout due to responsive state change',
          {
            isMobile,
            isTablet,
            autoCollapse: primary.autoCollapse,
          }
        )
      }
    }
  }, [isMobile, isTablet, primary.autoCollapse, clearHoverTimeout])

  // CRITICAL FIX: Handle tab switching to prevent stuck expanded state
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User switched away from tab - clear timeout and reset hover expansion
        clearHoverTimeout()
        if (primary.hoverExpanded) {
          setHoverExpanded(false)
          if (process.env.NODE_ENV === 'development') {
            console.log('[PrimarySidebar] Collapsed sidebar due to tab switch')
          }
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [clearHoverTimeout, primary.hoverExpanded, setHoverExpanded])

  // No initialization needed - store handles initial state correctly
  // useEffect removed: Store logic now properly initializes auto-collapse state

  // Determine visual state - expand if not collapsed OR if hover expanded
  const isVisuallyExpanded = !primary.collapsed || primary.hoverExpanded

  // Development mode state synchronization validation
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Validate that responsive state is synchronized
      const storeResponsiveState = {
        isMobile: storeIsMobile,
        isTablet: storeIsTablet,
      }

      const hookResponsiveState = {
        isMobile,
        isTablet,
      }

      // Check for synchronization issues
      if (
        storeResponsiveState.isMobile !== hookResponsiveState.isMobile ||
        storeResponsiveState.isTablet !== hookResponsiveState.isTablet
      ) {
        console.warn(
          '[PrimarySidebar] Responsive state synchronization mismatch:',
          {
            store: storeResponsiveState,
            hook: hookResponsiveState,
          }
        )
      }

      // Validate auto-collapse state consistency
      if (
        primary.autoCollapse &&
        primary.hoverExpanded &&
        !isAutoCollapseActive()
      ) {
        console.warn(
          '[PrimarySidebar] Invalid state: hoverExpanded is true but auto-collapse is not active'
        )
      }

      // Validate visual state calculation
      const expectedVisualState = !primary.collapsed || primary.hoverExpanded
      if (isVisuallyExpanded !== expectedVisualState) {
        console.warn('[PrimarySidebar] Visual state calculation mismatch:', {
          calculated: isVisuallyExpanded,
          expected: expectedVisualState,
          collapsed: primary.collapsed,
          hoverExpanded: primary.hoverExpanded,
        })
      }
    }
  }, [
    primary,
    isMobile,
    isTablet,
    storeIsMobile,
    storeIsTablet,
    isAutoCollapseActive,
    isVisuallyExpanded,
  ])

  const isProjectsActive =
    location.pathname.startsWith('/projects') || location.pathname === '/'

  // Debug logging for render and state
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[PrimarySidebar] 🔄 Component render state:', {
        autoCollapse: primary.autoCollapse,
        collapsed: primary.collapsed,
        hoverExpanded: primary.hoverExpanded,
        isMobile,
        isTablet,
        isAutoCollapseActive: isAutoCollapseActive(),
        isVisuallyExpanded,
        width: primary.width,
        user: user
          ? 'authenticated'
          : user === null
            ? 'unauthenticated'
            : 'loading',
      })
    }
  })

  if (user === undefined) {
    return (
      <div
        className={`bg-background border-r border-border flex flex-col transition-all duration-200 ease-out ${
          isVisuallyExpanded ? 'w-60 items-start' : 'w-16 items-center'
        } py-4`}
        data-slot="PrimarySidebar"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        role="navigation"
        aria-label="Primary navigation (loading)"
        aria-busy="true"
      >
        <div
          className={`${isVisuallyExpanded ? 'w-8 h-8 mx-4' : 'w-8 h-8 mx-auto'} bg-primary rounded-md flex items-center justify-center text-primary-foreground mb-6`}
          title="VRP System"
        >
          <Home className="w-5 h-5" aria-hidden="true" />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner className="w-5 h-5" />
        </div>
      </div>
    )
  }

  if (user === null) {
    return (
      <div
        className={`bg-background border-r border-border flex flex-col transition-all duration-200 ease-out ${
          isVisuallyExpanded ? 'w-60 items-start' : 'w-16 items-center'
        } py-4`}
        data-slot="PrimarySidebar"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        role="navigation"
        aria-label="Primary navigation (unauthenticated)"
      >
        <div
          className={`${isVisuallyExpanded ? 'w-8 h-8 mx-4' : 'w-8 h-8 mx-auto'} bg-primary rounded-md flex items-center justify-center text-primary-foreground mb-6`}
          title="VRP System"
        >
          <Home className="w-5 h-5" aria-hidden="true" />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <Button
            variant="ghost"
            className={`${isVisuallyExpanded ? 'w-full justify-start h-12 px-4' : 'w-12 h-12'} hover:bg-muted/50 hover:text-primary`}
            onClick={() => navigate('/auth/login')}
            title={!isVisuallyExpanded ? 'Sign In' : undefined}
            aria-label="Sign In"
            data-slot="auth-button"
          >
            <User className="w-5 h-5" aria-hidden="true" />
            {isVisuallyExpanded && (
              <span className="ml-3 text-sm font-normal">Sign In</span>
            )}
          </Button>
        </div>
      </div>
    )
  }

  // SBR-COORD-001: Primary Sidebar Container Structure
  return (
    <div
      className={`bg-background border-r border-border flex flex-col transition-all duration-200 ease-out ${
        isVisuallyExpanded ? 'w-60 items-start' : 'w-16 items-center'
      } py-4`}
      data-slot="PrimarySidebar"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseMove={e => {
        if (process.env.NODE_ENV === 'development') {
          console.log(
            '[PrimarySidebar] 🖱️ Mouse moving over sidebar',
            e.currentTarget === e.target
              ? 'on main element'
              : 'on child element'
          )
        }
      }}
      role="navigation"
      aria-label="Primary navigation"
      aria-expanded={isVisuallyExpanded}
      aria-describedby={primary.autoCollapse ? 'auto-collapse-help' : undefined}
    >
      {/* SBR-COORD-002: Primary Sidebar Header Section - Logo/Brand */}
      <div
        className={`${isVisuallyExpanded ? 'w-8 h-8 mx-4' : 'w-8 h-8 mx-auto'} bg-primary rounded-md flex items-center justify-center text-primary-foreground mb-6`}
        data-slot="brand-logo"
        title="VRP System"
      >
        <Home className="w-5 h-5" aria-hidden="true" />
      </div>

      {/* Development Debug Indicator */}
      {process.env.NODE_ENV === 'development' && (
        <div
          className={`${isVisuallyExpanded ? 'px-4 mb-4' : 'px-2 mb-4'} text-sm font-mono`}
        >
          <div className="bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-2 rounded">
            <div className="flex items-center gap-1 mb-2">
              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
              <span>AUTO-COLLAPSE DEBUG</span>
            </div>
            {isVisuallyExpanded && (
              <div className="text-[10px] space-y-1">
                <div>AC: {primary.autoCollapse ? 'ON' : 'OFF'}</div>
                <div>Collapsed: {primary.collapsed ? 'YES' : 'NO'}</div>
                <div>Hover: {primary.hoverExpanded ? 'YES' : 'NO'}</div>
                <div>Device: {isMobile ? 'MOB' : isTablet ? 'TAB' : 'DT'}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hidden accessibility helper text */}
      {primary.autoCollapse && (
        <div id="auto-collapse-help" className="sr-only" aria-live="polite">
          Auto-collapse is enabled. Hover or focus to expand the sidebar.
        </div>
      )}

      {/* SBR-COORD-003: Primary Sidebar Navigation Menu */}
      <nav
        className={`flex flex-col gap-2 ${isVisuallyExpanded ? 'w-full px-4' : 'items-center'}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <Button
          variant="ghost"
          className={`${
            isVisuallyExpanded ? 'w-full justify-start h-12 px-4' : 'w-12 h-12'
          } ${
            isProjectsActive
              ? 'bg-primary/10 text-primary border border-primary/20'
              : 'hover:bg-muted/50 hover:text-primary'
          }`}
          onClick={() => navigate('/projects')}
          title={!isVisuallyExpanded ? 'Projects' : undefined}
          aria-label="Projects"
          aria-current={isProjectsActive ? 'page' : undefined}
          data-slot="navigation-item"
        >
          {isVisuallyExpanded || isProjectsActive ? (
            <FolderOpen className="w-5 h-5" aria-hidden="true" />
          ) : (
            <Folder className="w-5 h-5" aria-hidden="true" />
          )}
          {isVisuallyExpanded && (
            <span className="ml-3 text-sm font-normal">Projects</span>
          )}
        </Button>

        {/* Toggle button - only show on desktop and when not in auto-collapse mode */}
        {!isMobile && !isTablet && !primary.autoCollapse && (
          <Button
            variant="ghost"
            className={`${
              isVisuallyExpanded
                ? 'w-full justify-start h-12 px-4 mt-2'
                : 'w-12 h-12 mt-2'
            } hover:bg-muted/50`}
            onClick={togglePrimary}
            title={primary.collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={
              primary.collapsed ? 'Expand sidebar' : 'Collapse sidebar'
            }
            data-slot="sidebar-toggle"
          >
            {/* SBR-COORD-004: Primary Sidebar Collapse/Expand Toggle */}
            {primary.collapsed ? (
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" aria-hidden="true" />
                <span className="ml-3 text-sm font-normal">Collapse</span>
              </>
            )}
          </Button>
        )}

        {/* Auto-collapse toggle - only show on desktop */}
        {!isMobile && !isTablet && (
          <Button
            variant="ghost"
            className={`${
              isVisuallyExpanded
                ? 'w-full justify-start h-12 px-4 mt-2'
                : 'w-12 h-12 mt-2'
            } hover:bg-muted/50 ${
              primary.autoCollapse
                ? 'bg-primary/5 text-primary border border-primary/10'
                : ''
            }`}
            onClick={() => setAutoCollapse(!primary.autoCollapse)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setAutoCollapse(!primary.autoCollapse)
              }
            }}
            title={
              primary.autoCollapse
                ? 'Disable auto-collapse (Pin sidebar)'
                : 'Enable auto-collapse (Hover to expand)'
            }
            aria-label={
              primary.autoCollapse
                ? 'Disable auto-collapse'
                : 'Enable auto-collapse'
            }
            aria-pressed={primary.autoCollapse}
            data-slot="auto-collapse-toggle"
          >
            {primary.autoCollapse ? (
              <PinOff className="w-5 h-5" aria-hidden="true" />
            ) : (
              <Pin className="w-5 h-5" aria-hidden="true" />
            )}
            {isVisuallyExpanded && (
              <span className="ml-3 text-sm font-normal">
                {primary.autoCollapse ? 'Auto-collapse' : 'Pin sidebar'}
              </span>
            )}
          </Button>
        )}
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* SBR-COORD-005: Primary Sidebar User Profile Section */}
      <div
        className={`flex flex-col gap-2 ${isVisuallyExpanded ? 'w-full px-4' : 'items-center'}`}
        role="region"
        aria-label="User account"
      >
        <Separator className={isVisuallyExpanded ? 'w-full' : 'w-8'} />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={`${
                isVisuallyExpanded
                  ? 'w-full justify-start h-12 px-4'
                  : 'w-12 h-12'
              } hover:bg-muted/50`}
              title={
                !isVisuallyExpanded
                  ? `${user?.name || user?.email || 'User'} - Profile`
                  : undefined
              }
              aria-label="User menu"
              data-slot="user-menu-trigger"
            >
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-normal text-primary">
                  {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
              {isVisuallyExpanded && (
                <div className="ml-3 flex flex-col items-start text-left overflow-hidden">
                  <span className="text-sm font-normal truncate max-w-full text-foreground">
                    {user?.name || 'User'}
                  </span>
                  <span className="text-sm text-muted-foreground truncate max-w-full">
                    {user?.email}
                  </span>
                </div>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="right"
            align="end"
            className="w-56"
            data-slot="user-menu"
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-normal leading-none text-foreground">
                  {user?.name || 'User'}
                </p>
                <p className="text-sm leading-none text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
              <span>Settings</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} disabled={isSigningOut}>
              {isSigningOut ? (
                <LoadingSpinner className="mr-2 h-4 w-4" aria-hidden="true" />
              ) : (
                <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              <span>{isSigningOut ? 'Signing out...' : 'Sign out'}</span>
              {!isSigningOut && (
                <span className="ml-auto text-sm text-muted-foreground">
                  Ctrl+Shift+Q
                </span>
              )}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

export default PrimarySidebar
